/** @jsx jsx */
import { jsx } from "@emotion/core";
import * as React from "react";
import { KYCDetailsProps } from "./interfaces";
import { kycDetailsStyle } from "./styles";
import { responsiveStyles } from "../../commons/styles";
import { RegistrationLayout } from "../../components/RegistrationLayout";
import { InputField } from "../../atoms/InputField";
import { RegistrationFooterButton } from "../../components/RegistrationFooterButton";
import {
  ButtonClickType,
  UploadTypes,
  allowedFileTypes,
  regexValidation,
  inputFieldOptionalLabel,
  inputFieldMandatoryLabel,
} from "../../constants/commons";
import * as _ from "lodash";
import { UploadComp } from "../../molecules/Upload";
import { useIsMobile, useIsTab } from "../../hooks/useIsMobile";
import useSTR from "react-str";
import { deleteData, getData, postData } from "../../utils/fetchers";
import { URL } from "../../API/urlEndpoints";
import { ShowMessage } from "../../components/ShowMessage";
import {
  checkIfUploadIsPresentOfType,
  getErrorForField,
  getFieldConfigStatus,
  getImgURLFromBufferData,
  isFieldOptional,
  showDisplayHeadingAndContentFromLoadingStatus,
} from "../../utils/helpers";
import {
  FieldRenderState,
  KYCDetailsPost,
  RegisterationFields,
} from "../../commons/interfaces";
import { useUserContext } from "../../hooks/cookieHooks";
import { toast } from "../../utils/toast";
import { ErrorComponent } from "../../molecules/ErrorComponent";
import errorLogo from "../../assets/error-icon.svg";
import clearAndVisibleMobileLogo from "../../assets/upload-document-clearandvisible-mobile.png";
import clearAndVisibleDesktopLogo from "../../assets/upload-document-clearandvisible-desktop.png";
import doNotUseRuledPageDesktopLogo from "../../assets/upload-document-ruledpage-desktop.png";
import doNotUseRuledPageMobileLogo from "../../assets/upload-document-ruledpage-mobile.png";
import photoOfAnyOtherPageDesktopLogo from "../../assets/upload-document-anyotherpaper-desktop.png";
import photoOfAnyOtherPageMobileLogo from "../../assets/upload-document-anyotherpaper-mobile.png";

export const KYCDetailsPage: React.FunctionComponent<KYCDetailsProps> = (
  props
) => {
  const {
    onSaveAndExitClick,
    onBackButtonClick,
    SuccessFullSaveObj,
    pageNavigationDetails,
    onNextClick,
  } = props;
  const { nextScreen } = pageNavigationDetails;
  const isMobileView = useIsMobile();
  const isTabView = useIsTab();
  // State to Maintain PAN details
  const [panNumber, setPanNumber] = React.useState<string>();
  // State to Maintain Aadhar Card details
  const [aadharCard, setAadharCard] = React.useState<string>();
  // State to maintain whether the Aadhar card is valid or not
  const [
    isEnteredAadharNumberValid,
    setIsEnteredAadharNumberValid,
  ] = React.useState<boolean>();
  // State to maintain whether the PAN card is valid or not
  const [isEnteredPANNumberValid, setIsEnteredPANNumberValid] = React.useState<
    boolean
  >();
  // State checking whether user can be navigated to the next page or not
  const [isNextPageNavAllowed, setIsNextPageNavAllowed] = React.useState<
    boolean
  >(false);
  //State to maintain the uploaded docs/images
  const [successfullUploadedDocs, setSuccessfullUploadedDocs] = React.useState<
    any[]
  >([]);
  // State to maintain whether the Medical Certificate is uploaded or not
  const [isAadharUploaded, setIsAadharUploaded] = React.useState<boolean>();
  // State to maintain whether the Medical Certificate is uploaded or not
  const [consultationFee, setConsultationFee] = React.useState<number>();
  // State to maintain whether the Scanned Signature is uploaded or not
  const [
    isScannedSignatureUploaded,
    setIsScannedSignatureUploaded,
  ] = React.useState<boolean>();
  // Calling the SSO Hook to retrieve user information of the logged in user
  const user = useUserContext();

  // [GET] On page load
  const [
    ,
    {
      result: getKYCDataResult,
      revalidator: refetchKYCData,
      loading: loadingKYCData,
      error: errorKYCData,
    },
  ] = useSTR(getData);

  // [POST] For Saving data on page.
  const [
    ,
    {
      result: postKYCDataResult,
      revalidator: refetchPostKYCData,
      loading: postKYCData,
      error: errortPostKYCData,
    },
  ] = useSTR(postData);

  // [DELETE] For removing the uploaded Image
  const [
    ,
    {
      result: removeUploadedAsset,
      revalidator: refetchDeleteUploadedAsset,
      loading: loadingRemovedAsset,
      error: errorRemovedAsset,
    },
  ] = useSTR(deleteData);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // On Page load, calling the GET API call
  React.useEffect(() => {
    refetchKYCData(`${URL.getKYCDetails(_.get(user, "empId"))}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Setting respective state variables once data is fetched
  React.useEffect(() => {
    if (getKYCDataResult) {
      const {
        panNumber,
        aadharNumber,
        aadharCard,
        consultationFee,
      } = getKYCDataResult;
      if (panNumber) {
        setPanNumber(panNumber);
        setIsEnteredPANNumberValid(true);
      }
      consultationFee && setConsultationFee(consultationFee);
      if (aadharNumber) {
        setAadharCard(aadharNumber);
        setIsEnteredAadharNumberValid(true);
      }

      // To get the selected Documents from the server on Page load
      let documentsFromServer = getImgURLFromBufferData(aadharCard);
      documentsFromServer && setSuccessfullUploadedDocs(documentsFromServer);
    }
  }, [getKYCDataResult]);

  // Showing the success message in a toast component / Fetching data From Server
  React.useEffect(() => {
    if (postKYCDataResult) {
      postKYCDataResult?.message && toast.success(postKYCDataResult.message);
      if (isNextPageNavAllowed) {
        onNextClick(nextScreen);
      } else {
        SuccessFullSaveObj(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postKYCDataResult, isNextPageNavAllowed]);

  // Function which is called when PAN number is changed
  const onPanNumberChange = (enteredPanNumber: string) => {
    setPanNumber(enteredPanNumber);
  };

  // Function which is called when Consultation Fee is changed
  const onConsultationFeeChange = (consultationFee: string) => {
    setConsultationFee(parseInt(consultationFee));
  };

  // Function which is called when Aadhar Card number is changed
  const onAadharCardChange = (enteredAadharNumber: string) => {
    setAadharCard(enteredAadharNumber);
  };

  // Callback from the parent Component to SAVE AND QUIT
  React.useEffect(() => {
    onSaveAndExitClick && onPositiveActionClick(ButtonClickType.SAVE_AND_EXIT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveAndExitClick]);

  // Function which decides whether the control should be re-directed to the next sreen or just SAVE
  const onPositiveActionClick = (buttonClickType: ButtonClickType) => {
    const kycDetailsObj = constructDataForPOSTAPI();

    switch (buttonClickType) {
      case ButtonClickType.NEXT:
        let paramsObj = {
          ...kycDetailsObj,
          customHeader: { change_state: true },
        };
        // POST CALL
        refetchPostKYCData(
          `${URL.postKYCDetails(_.get(user, "empId"))}`,
          paramsObj
        );
        setIsNextPageNavAllowed(true);
        break;

      case ButtonClickType.SAVE_AND_EXIT:
        // POST CALL
        refetchPostKYCData(
          `${URL.postKYCDetails(_.get(user, "empId"))}`,
          kycDetailsObj
        );
        setIsNextPageNavAllowed(false);
        break;

      default:
        break;
    }
  };

  // Function which constructs the necessary JSON as per the API call
  const constructDataForPOSTAPI = React.useCallback(() => {
    let kycDetailsObj: KYCDetailsPost = {
      panNumber: panNumber as string,
      aadharNumber: aadharCard as string,
      consultationFee: consultationFee as number,
    };
    return kycDetailsObj;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panNumber, aadharCard, consultationFee]);

  // Function which is called when the user Delete's a selected Uploaded file
  const removeSelectedAsset = (removedAsset: any) => {
    const { filename, id } = removedAsset;
    refetchDeleteUploadedAsset(
      `${URL.removeUploadedAsset(_.get(user, "empId"), id, filename)}`
    );
    // Filtering and removing the DELETED Asset from the ASSET's Collection
    setSuccessfullUploadedDocs(
      successfullUploadedDocs.filter(
        (uploadedDocs) => uploadedDocs !== removedAsset
      )
    );
  };

  // Function which is executed after successfully removing the uploaded Asset
  React.useEffect(() => {
    if (removeUploadedAsset && removeUploadedAsset.success) {
      toast.success(removeUploadedAsset.message);
    }
  }, [removeUploadedAsset]);

  // Function called whenever the user successfully uploads an asset
  const onSuccessfullFileUpload = (onSuccessFileUploadObj: any) => {
    setSuccessfullUploadedDocs((oldUploadedAsset) => [
      ...oldUploadedAsset,
      onSuccessFileUploadObj,
    ]);
  };

  // Function which let's us know whether the PAN card is valid or not
  const isPanCardValid = (isValid: boolean) => {
    setIsEnteredPANNumberValid(isValid);
  };

  // Function which let's us know whether the Aadhar card is valid or not
  const isAadharCardValid = (isValid: boolean) => {
    setIsEnteredAadharNumberValid(isValid);
  };

  // Variable which shows the loading state
  const isScreenLoading = React.useMemo(() => {
    return loadingKYCData || postKYCData || loadingRemovedAsset;
  }, [loadingKYCData, postKYCData, loadingRemovedAsset]);

  // Hook which is called whenever there is an error in either GET/POST calls
  const showErrorComponent = React.useMemo(() => {
    return errorRemovedAsset || errorKYCData || errortPostKYCData;
  }, [errortPostKYCData, errorKYCData, errorRemovedAsset]);

  // For showing/hiding the AADHAR_NUMBER
  const showAadharNumberField = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.fieldsconfig &&
      _.keys(getKYCDataResult.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getKYCDataResult.fieldsconfig,
        RegisterationFields.AADHAR_NUMBER
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getKYCDataResult]);

  // For showing the label OPTIONAL for the field [AADHAR_NUMBER]
  const showAadharNumberLabel = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.fieldsconfig &&
      _.keys(getKYCDataResult.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getKYCDataResult.fieldsconfig,
          RegisterationFields.AADHAR_NUMBER
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getKYCDataResult]);

  // For showing errors [AADHAR_NUMBER]
  const aadharNumberError = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.errors &&
      getErrorForField(
        RegisterationFields.AADHAR_NUMBER,
        getKYCDataResult.errors
      )
    );
  }, [getKYCDataResult]);

  // For showing/hiding the CONSULTATION FEES
  const showConsultationFeeField = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.fieldsconfig &&
      _.keys(getKYCDataResult.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getKYCDataResult.fieldsconfig,
        RegisterationFields.CONSULTATION_FEE
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getKYCDataResult]);

  // For showing the label OPTIONAL for the field [CONSULTATION FEES]
  const showConsultationFeeOptionalLabel = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.fieldsconfig &&
      _.keys(getKYCDataResult.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getKYCDataResult.fieldsconfig,
          RegisterationFields.CONSULTATION_FEE
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getKYCDataResult]);

  // For showing errors [CONSULTATION FEES]
  const consultationFeeError = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.errors &&
      getErrorForField(
        RegisterationFields.CONSULTATION_FEE,
        getKYCDataResult.errors
      )
    );
  }, [getKYCDataResult]);

  // For showing/hiding the AADHAR_NUMBERAADHAR
  const showAadharNumberFieldUpload = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.fieldsconfig &&
      _.keys(getKYCDataResult.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getKYCDataResult.fieldsconfig,
        RegisterationFields.AADHAR_NUMBER_UPLOAD
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getKYCDataResult]);

  // For checking whether the AADHAR_NUMBER is a mandatory field or not
  const isAadharNumberFieldUploadMandatoryField = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.fieldsconfig &&
      _.keys(getKYCDataResult.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getKYCDataResult.fieldsconfig,
        RegisterationFields.AADHAR_NUMBER_UPLOAD
      )[0] === FieldRenderState.MANDATORY
    );
  }, [getKYCDataResult]);

  // For showing errors [AADHAR_NUMBER_UPLOAD]
  const aadharUploadError = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.errors &&
      getErrorForField(
        RegisterationFields.AADHAR_NUMBER_UPLOAD,
        getKYCDataResult.errors
      )
    );
  }, [getKYCDataResult]);

  // For showing the label OPTIONAL for the field [AADHAR_NUMBER]
  const showAadharNumberFieldUploadLabel = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.fieldsconfig &&
      _.keys(getKYCDataResult.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getKYCDataResult.fieldsconfig,
          RegisterationFields.AADHAR_NUMBER_UPLOAD
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getKYCDataResult]);

  // For showing/hiding the Pan Number
  const showPanNumberField = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.fieldsconfig &&
      _.keys(getKYCDataResult.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getKYCDataResult.fieldsconfig,
        RegisterationFields.PAN_NUMBER
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getKYCDataResult]);

  // For showing the label OPTIONAL for the field [Pan Number]
  const showPanNumberFieldLabel = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.fieldsconfig &&
      _.keys(getKYCDataResult.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getKYCDataResult.fieldsconfig,
          RegisterationFields.PAN_NUMBER
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getKYCDataResult]);

  // For showing errors [PAN_NUMBER]
  const panNumberError = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.errors &&
      getErrorForField(RegisterationFields.PAN_NUMBER, getKYCDataResult.errors)
    );
  }, [getKYCDataResult]);

  // For showing/hiding the Scanned Signature Upload
  const showScannedSignatureUploadField = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.fieldsconfig &&
      _.keys(getKYCDataResult.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getKYCDataResult.fieldsconfig,
        RegisterationFields.SCANNED_SIGNATURE_UPLOAD
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getKYCDataResult]);

  // For showing the label OPTIONAL for the field Scanned Signature Upload
  const showScannedSignatureUploadLabel = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.fieldsconfig &&
      _.keys(getKYCDataResult.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getKYCDataResult.fieldsconfig,
          RegisterationFields.SCANNED_SIGNATURE_UPLOAD
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getKYCDataResult]);

  // For showing errors [SCANNED_SIGNATURE_UPLOAD]
  const scannedSignatureUploadError = React.useMemo(() => {
    return (
      getKYCDataResult &&
      getKYCDataResult.errors &&
      getErrorForField(
        RegisterationFields.SCANNED_SIGNATURE_UPLOAD,
        getKYCDataResult.errors
      )
    );
  }, [getKYCDataResult]);

  // Function which allows the user to go to the next screen
  const isSubmitAllowed = React.useMemo(() => {
    return !(
      panNumber &&
      aadharCard &&
      isEnteredAadharNumberValid &&
      isEnteredPANNumberValid &&
      (isAadharNumberFieldUploadMandatoryField ? isAadharUploaded : true) &&
      (showScannedSignatureUploadField ? isScannedSignatureUploaded : true)
    );
  }, [
    panNumber,
    isAadharUploaded,
    showScannedSignatureUploadField,
    isScannedSignatureUploaded,
    aadharCard,
    isEnteredPANNumberValid,
    isEnteredAadharNumberValid,
    isAadharNumberFieldUploadMandatoryField,
  ]);

  // Hook which is called when an upload is successfully added/removed.
  // Updating the state here so that the submit button could be enabled/disabled based on the Aadhar Card Upload
  React.useEffect(() => {
    setIsAadharUploaded(
      checkIfUploadIsPresentOfType(
        UploadTypes.AADHAR_CARD,
        successfullUploadedDocs
      )[0]
    );
  }, [successfullUploadedDocs]);

  // Hook which is called when an upload is successfully added/removed.
  // Updating the state here so that the submit button could be enabled/disabled based on the Scanned Signature Upload
  React.useEffect(() => {
    setIsScannedSignatureUploaded(
      checkIfUploadIsPresentOfType(
        UploadTypes.SCANNED_SIGNATURE,
        successfullUploadedDocs
      )[0]
    );
  }, [successfullUploadedDocs]);

  return (
    <span css={[responsiveStyles, kycDetailsStyle]}>
      <ShowMessage
        visible={isScreenLoading}
        loading={isScreenLoading}
        heading={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: loadingKYCData,
            postLoginScreen: postKYCData,
          })?.loadingHeading
        }
        description={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: loadingKYCData,
            postLoginScreen: postKYCData,
          })?.loadingDescription
        }
      />
      <RegistrationLayout>
        <h2>KYC Details</h2>
        {showPanNumberField && (
          <InputField
            capitaliseInput
            placeholder={`PAN number ${
              showPanNumberFieldLabel
                ? inputFieldOptionalLabel
                : inputFieldMandatoryLabel
            }`}
            type="text"
            validate={isPanCardValid}
            initialValue={panNumber}
            validationRegEx={regexValidation.panNumber}
            validationErrMsg={
              panNumberError
                ? panNumberError
                : errortPostKYCData?.meta?.panError ||
                  "Please enter a valid PAN number"
            }
            onChange={onPanNumberChange}
          />
        )}

        {showAadharNumberField && (
          <InputField
            placeholder={`Aadhar number ${
              showAadharNumberLabel
                ? inputFieldOptionalLabel
                : inputFieldMandatoryLabel
            }`}
            type="number"
            validate={isAadharCardValid}
            validationRegEx={regexValidation.aadharNumber}
            validationErrMsg={
              aadharNumberError
                ? aadharNumberError
                : errortPostKYCData?.meta?.aadharError ||
                  "Please enter a valid aadhar number"
            }
            initialValue={aadharCard}
            onChange={onAadharCardChange}
          />
        )}

        {showConsultationFeeField && (
          <InputField
            placeholder={`Enter consultation fee in rupees ${
              showConsultationFeeOptionalLabel
                ? inputFieldOptionalLabel
                : inputFieldMandatoryLabel
            }`}
            type="number"
            validationErrMsg={consultationFeeError}
            initialValue={consultationFee}
            onChange={onConsultationFeeChange}
          />
        )}

        {showAadharNumberFieldUpload && (
          <div
            className={`resposnive-container-margin ${
              isMobileView ? "mb-5" : "mb-3"
            }`}
          >
            <div className="d-flex">
              <h6>Upload Aadhar Card</h6>
              <div className="small-text ml-2">
                {showAadharNumberFieldUploadLabel ? (
                  showAadharNumberFieldUploadLabel
                ) : (
                  <div className="responsive-error-asterix-style">
                    {inputFieldMandatoryLabel}
                  </div>
                )}
              </div>
            </div>

            <div className="short-description-style mb-3">
              Make sure to add both the sides of the certificate
            </div>
            <div className={`col-12 ${isMobileView ? "p-0" : "p-0"}`}>
              <UploadComp
                onSuccess={onSuccessfullFileUpload}
                uploadType={UploadTypes.AADHAR_CARD}
                removeSelectedAsset={removeSelectedAsset}
                uploadedFiles={successfullUploadedDocs}
                allowedFilesType={[
                  allowedFileTypes.jpeg,
                  allowedFileTypes.jpg,
                  allowedFileTypes.png,
                  allowedFileTypes.pdf,
                ]}
                allowedFileSize={10}
              />
              {aadharUploadError && (
                <div className="error-text">{aadharUploadError}</div>
              )}
            </div>
          </div>
        )}

        {showScannedSignatureUploadField && (
          <div className={`mt-3 ${isMobileView ? "mb-5" : "mb-3"}`}>
            <div className="d-flex">
              <h6>Scanned Signature</h6>
              <div className="small-text ml-2">
                {showScannedSignatureUploadLabel ? (
                  showScannedSignatureUploadLabel
                ) : (
                  <div className="responsive-error-asterix-style">
                    {inputFieldMandatoryLabel}
                  </div>
                )}
              </div>
            </div>
            <div className="short-description-style mb-3">
              Please sign on a white blank paper and upload a scanned copy /
              image of the same as per below instruction. This signature will be
              added in the digital prescription that you will be generating via
              our EMR tool while providing your consultation to our customers.
            </div>
            <div className={`col-12 ${isMobileView ? "p-0" : "p-0"}`}>
              <UploadComp
                onSuccess={onSuccessfullFileUpload}
                uploadType={UploadTypes.SCANNED_SIGNATURE}
                removeSelectedAsset={removeSelectedAsset}
                uploadedFiles={successfullUploadedDocs}
                allowedFilesType={[
                  allowedFileTypes.jpeg,
                  allowedFileTypes.jpg,
                  allowedFileTypes.png,
                  allowedFileTypes.pdf,
                ]}
                allowedFileSize={10}
              />
              {scannedSignatureUploadError && (
                <div className="error-text">{scannedSignatureUploadError}</div>
              )}
            </div>
          </div>
        )}
        {showScannedSignatureUploadField && (
          <div
            className={` ${
              isMobileView || isTabView ? "custom-card-div py-4 px-2" : "mt-3"
            }`}
          >
            <div className="d-flex justify-content-center align-items-center">
              <div>
                <img
                  src={errorLogo}
                  alt=""
                  className="logo-responsive-styling"
                />
              </div>
              <div className="ml-2 description-style d-flex align-items-center">
                Make sure you use a black/blue pen only
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between mt-2">
              <div className="d-flex flex-column">
                <div className="mx-2 text-center">
                  <img
                    src={` ${
                      isMobileView
                        ? clearAndVisibleMobileLogo
                        : clearAndVisibleDesktopLogo
                    }`}
                    alt=""
                    width="100%"
                  />
                  <div className="image-description">
                    Clear and Visible image
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column">
                <div className="mx-2 text-center">
                  <img
                    src={` ${
                      isMobileView
                        ? doNotUseRuledPageMobileLogo
                        : doNotUseRuledPageDesktopLogo
                    }`}
                    alt=""
                    width="100%"
                  />
                  <div className="image-description-2">
                    Do not use ruled page
                  </div>
                </div>
              </div>
              <div className="d-flex flex-column">
                <div className="mx-2 text-center">
                  <img
                    src={` ${
                      isMobileView
                        ? photoOfAnyOtherPageMobileLogo
                        : photoOfAnyOtherPageDesktopLogo
                    }`}
                    alt=""
                    width="100%"
                  />
                  <div className="image-description-2">
                    Photograph of any other paper
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showErrorComponent && <ErrorComponent errorObj={showErrorComponent} />}
        <div
          className={`d-flex align-items-center justify-content-center my-4 p-4 ${
            isMobileView
              ? "internet-connectivity-block-info"
              : "internet-connectivity-block-info-tabmonitor"
          }`}
        >
          <div className="d-flex align-items-start">
            <img src={errorLogo} alt="" className="logo-responsive-styling" />
            <div className="ml-2 display-content-style">
              We’ll check your PAN and Aadhar details for verification
              automatically
            </div>
          </div>
        </div>
      </RegistrationLayout>
      <RegistrationFooterButton
        disableNextButton={isSubmitAllowed}
        onPositiveActionClick={onPositiveActionClick}
        onBackButtonClick={onBackButtonClick}
      />
    </span>
  );
};
